import React, { useState, useRef } from 'react';
import { getToken, setUser } from '../../utils/auth';
import { toast } from 'react-toastify';
import {
  X,
  User,
  Phone,
  Mail,
  Shield,
  Camera,
  Lock,
  Eye,
  EyeOff,
  Save,
  Calendar,
} from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const TAB = { PROFILE: 'profile', PASSWORD: 'password' };

const ProfileModal = ({ user, onClose, onUpdated }) => {
  const [tab, setTab]             = useState(TAB.PROFILE);
  const [loading, setLoading]     = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileRef = useRef();

  // Profile form
  const [profile, setProfile] = useState({
    name:  user?.name  || '',
    phone: user?.phone || '',
    photo: null,
  });

  // Password form
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [showPwd, setShowPwd] = useState({ current: false, next: false, confirm: false });

  /* ── helpers ── */
  const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProfile(p => ({ ...p, photo: file }));
    setPhotoPreview(URL.createObjectURL(file));
  };

  /* ── save profile ── */
  const saveProfile = async (e) => {
    e.preventDefault();
    if (!profile.name.trim()) { toast.error('Name cannot be empty'); return; }
    setLoading(true);
    try {
      const body = new FormData();
      body.append('name', profile.name.trim());
      body.append('phone', profile.phone.trim());
      if (profile.photo) body.append('photo', profile.photo);

      const res = await fetch(`${API}/api/auth/profile`, {
        method: 'PUT',
        headers: authHeaders(),
        body,
      });
      const data = await res.json();
      if (data.success) {
        setUser(data.user);
        toast.success('Profile updated');
        onUpdated(data.user);
      } else {
        toast.error(data.message || 'Update failed');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  };

  /* ── change password ── */
  const changePassword = async (e) => {
    e.preventDefault();
    if (pwd.next.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (pwd.next !== pwd.confirm) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/password`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword: pwd.current, newPassword: pwd.next }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Password changed successfully');
        setPwd({ current: '', next: '', confirm: '' });
      } else {
        toast.error(data.message || 'Failed to change password');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  };

  /* ── shared input styles ── */
  const inputBase = "w-full pl-10 pr-4 py-2.5 rounded-xl border text-sm outline-none transition-all";
  const inputStyle = { borderColor: '#BFC9D1', backgroundColor: '#EAEFEF', color: '#25343F' };
  const onFocus = e => { e.target.style.borderColor = '#FF9B51'; e.target.style.boxShadow = '0 0 0 3px rgba(255,155,81,0.12)'; };
  const onBlur  = e => { e.target.style.borderColor = '#BFC9D1'; e.target.style.boxShadow = 'none'; };

  const photoSrc = photoPreview
    ? photoPreview
    : user?.photoId
      ? `${API}/api/users/${user._id}/photo`
      : null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      {/* Scroll container */}
      <div className="flex min-h-full items-start sm:items-center justify-center p-4">
      <div
        className="relative bg-white rounded-2xl w-full max-w-md shadow-brand my-4"
        style={{ border: '1px solid #BFC9D1' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: '#EAEFEF' }}>
          <h2 className="text-lg font-bold" style={{ color: '#25343F' }}>My Profile</h2>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 transition-colors" style={{ color: '#BFC9D1' }}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Avatar + info strip */}
        <div className="px-6 pt-5 pb-4 flex items-center gap-4" style={{ backgroundColor: '#EAEFEF' }}>
          <div className="relative flex-shrink-0">
            <div className="w-16 h-16 rounded-2xl overflow-hidden flex items-center justify-center text-white text-2xl font-bold"
              style={{ background: 'linear-gradient(135deg,#FF9B51,#e07a33)' }}>
              {photoSrc
                ? <img src={photoSrc} alt="avatar" className="w-full h-full object-cover" />
                : (user?.name?.[0]?.toUpperCase() || <User className="w-7 h-7" />)
              }
            </div>
            {tab === TAB.PROFILE && (
              <button
                onClick={() => fileRef.current.click()}
                className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-white shadow"
                style={{ backgroundColor: '#FF9B51' }}
                title="Change photo"
              >
                <Camera className="w-3.5 h-3.5" />
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
          </div>
          <div className="min-w-0">
            <p className="font-bold text-base truncate" style={{ color: '#25343F' }}>{user?.name}</p>
            <p className="text-xs" style={{ color: '#BFC9D1' }}>@{user?.username}</p>
            <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-semibold capitalize"
              style={{ backgroundColor: 'rgba(37,52,63,0.1)', color: '#25343F' }}>
              <Shield className="w-3 h-3" />{user?.role}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b px-6" style={{ borderColor: '#EAEFEF' }}>
          {[{ id: TAB.PROFILE, label: 'Profile' }, { id: TAB.PASSWORD, label: 'Password' }].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className="py-3 mr-6 text-sm font-semibold border-b-2 transition-colors"
              style={tab === t.id
                ? { borderColor: '#FF9B51', color: '#FF9B51' }
                : { borderColor: 'transparent', color: '#BFC9D1' }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Profile Tab ── */}
        {tab === TAB.PROFILE && (
          <form onSubmit={saveProfile} className="px-6 py-5 space-y-4">

            {/* Read-only fields */}
            <div className="space-y-3 pb-3 border-b" style={{ borderColor: '#EAEFEF' }}>
              <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl" style={{ backgroundColor: '#EAEFEF' }}>
                <Mail className="w-4 h-4 flex-shrink-0" style={{ color: '#BFC9D1' }} />
                <div>
                  <p className="text-xs" style={{ color: '#BFC9D1' }}>Email</p>
                  <p className="text-sm font-medium" style={{ color: '#25343F' }}>{user?.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl" style={{ backgroundColor: '#EAEFEF' }}>
                <Calendar className="w-4 h-4 flex-shrink-0" style={{ color: '#BFC9D1' }} />
                <div>
                  <p className="text-xs" style={{ color: '#BFC9D1' }}>Member since</p>
                  <p className="text-sm font-medium" style={{ color: '#25343F' }}>
                    {user?.createdAt
                      ? new Date(user.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
                      : '—'}
                  </p>
                </div>
              </div>
            </div>

            {/* Editable fields */}
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: '#25343F' }}>Full Name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#BFC9D1' }} />
                <input
                  type="text" value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))}
                  className={inputBase} style={inputStyle} onFocus={onFocus} onBlur={onBlur}
                  placeholder="Your full name"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: '#25343F' }}>Phone Number</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#BFC9D1' }} />
                <input
                  type="tel" value={profile.phone} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))}
                  className={inputBase} style={inputStyle} onFocus={onFocus} onBlur={onBlur}
                  placeholder="+91 98765 43210"
                />
              </div>
            </div>

            {photoPreview && (
              <p className="text-xs px-3 py-2 rounded-xl" style={{ backgroundColor: 'rgba(255,155,81,0.1)', color: '#FF9B51' }}>
                New photo selected — will be saved on submit.
              </p>
            )}

            <button
              type="submit" disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-95 disabled:opacity-50 shadow-accent"
              style={{ backgroundColor: '#FF9B51' }}
            >
              {loading
                ? <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                : <><Save className="w-4 h-4" />Save Changes</>
              }
            </button>
          </form>
        )}

        {/* ── Password Tab ── */}
        {tab === TAB.PASSWORD && (
          <form onSubmit={changePassword} className="px-6 py-5 space-y-4">
            {[
              { key: 'current', label: 'Current Password',  placeholder: 'Enter current password' },
              { key: 'next',    label: 'New Password',       placeholder: 'Min. 8 characters' },
              { key: 'confirm', label: 'Confirm New Password', placeholder: 'Repeat new password' },
            ].map(({ key, label, placeholder }) => (
              <div key={key}>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: '#25343F' }}>{label}</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#BFC9D1' }} />
                  <input
                    type={showPwd[key] ? 'text' : 'password'}
                    value={pwd[key]}
                    onChange={e => setPwd(p => ({ ...p, [key]: e.target.value }))}
                    className={`${inputBase} pr-10`} style={inputStyle}
                    onFocus={onFocus} onBlur={onBlur}
                    placeholder={placeholder}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(s => ({ ...s, [key]: !s[key] }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 transition-opacity hover:opacity-70"
                    style={{ color: '#BFC9D1' }}
                  >
                    {showPwd[key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            ))}

            <button
              type="submit" disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-95 disabled:opacity-50"
              style={{ backgroundColor: '#25343F' }}
            >
              {loading
                ? <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                : <><Lock className="w-4 h-4" />Update Password</>
              }
            </button>
          </form>
        )}
      </div>
      </div>
    </div>
  );
};

export default ProfileModal;
